import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import JSONLD from '@/components/feature/JSONLD';
import { getArticleBySlug, getRecentArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = slug
    ? getRecentArticles(6).filter((a) => a.slug !== slug).slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.kaiyun-login.com/' },
      ...(category ? [{ '@type': 'ListItem', position: 2, name: category.name, item: `https://www.kaiyun-login.com/category/${article.category}` }] : []),
      { '@type': 'ListItem', position: category ? 3 : 2, name: article.title },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: 'zh-CN',
  };

  const articleImages: Record<string, string> = {
    'article-security-001': 'https://readdy.ai/api/search-image?query=Chinese%20technology%20blog%20article%20detail%20header%20image%20about%20login%20security%20protection%2C%20warm%20minimalist%20style%20with%20soft%20lighting%2C%20clean%20background%2C%20professional%20atmosphere%2C%20shield%20concept%2C%20warm%20coral%20tones%2C%20corporate%20blog%20aesthetic%2C%20high%20resolution&width=1200&height=600&seq=detail-article-security-001&orientation=landscape',
    'article-device-002': 'https://readdy.ai/api/search-image?query=Chinese%20technology%20blog%20article%20detail%20header%20image%20about%20multi%20device%20login%2C%20warm%20minimalist%20style%20with%20soft%20lighting%2C%20clean%20background%2C%20smartphone%20and%20laptop%2C%20warm%20amber%20tones%2C%20corporate%20blog%20aesthetic%2C%20high%20resolution&width=1200&height=600&seq=detail-article-device-002&orientation=landscape',
    'article-update-003': 'https://readdy.ai/api/search-image?query=Chinese%20technology%20blog%20article%20detail%20header%20image%20about%20software%20update%20version%205%2C%20warm%20minimalist%20style%20with%20soft%20lighting%2C%20clean%20background%2C%20modern%20interface%20concept%2C%20warm%20coral%20tones%2C%20corporate%20blog%20aesthetic%2C%20high%20resolution&width=1200&height=600&seq=detail-article-update-003&orientation=landscape',
    'article-enterprise-004': 'https://readdy.ai/api/search-image?query=Chinese%20technology%20blog%20article%20detail%20header%20image%20about%20enterprise%20solution%2C%20warm%20minimalist%20style%20with%20soft%20lighting%2C%20clean%20background%2C%20business%20team%20concept%2C%20warm%20amber%20tones%2C%20corporate%20blog%20aesthetic%2C%20high%20resolution&width=1200&height=600&seq=detail-article-enterprise-004&orientation=landscape',
    'article-faq-005': 'https://readdy.ai/api/search-image?query=Chinese%20technology%20blog%20article%20detail%20header%20image%20about%20account%20security%20FAQ%2C%20warm%20minimalist%20style%20with%20soft%20lighting%2C%20clean%20background%2C%20question%20answer%20concept%2C%20warm%20coral%20tones%2C%20corporate%20blog%20aesthetic%2C%20high%20resolution&width=1200&height=600&seq=detail-article-faq-005&orientation=landscape',
    'article-trend-006': 'https://readdy.ai/api/search-image?query=Chinese%20technology%20blog%20article%20detail%20header%20image%20about%20industry%20trend%202026%2C%20warm%20minimalist%20style%20with%20soft%20lighting%2C%20clean%20background%2C%20future%20technology%20concept%2C%20warm%20amber%20tones%2C%20corporate%20blog%20aesthetic%2C%20high%20resolution&width=1200&height=600&seq=detail-article-trend-006&orientation=landscape',
  };

  const breadcrumbItems = category
    ? [{ label: category.name, path: `/category/${article.category}` }, { label: article.title }]
    : [{ label: article.title }];

  return (
    <>
      <ScrollToTop />
      <JSONLD data={breadcrumbSchema} />
      <JSONLD data={articleSchema} />
      <SEOHead
        title={`${article.title} - 开云登录`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.kaiyun-login.com/article/${slug}`}
        ogType="article"
      />

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <article className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500 mb-4">
                <span className="bg-primary-100/80 text-primary-700 px-3 py-1 rounded-full font-medium whitespace-nowrap">
                  {category?.name || '未分类'}
                </span>
                <span className="whitespace-nowrap">{article.date}</span>
                <span className="whitespace-nowrap">{article.readTime}阅读</span>
                <span className="whitespace-nowrap">作者：{article.author}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={articleImages[article.imageSeq] || articleImages['article-security-001']}
                alt={article.title}
                className="w-full h-64 md:h-80 object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                prose-h2:text-xl prose-h2:md:text-2xl prose-h2:mt-10 prose-h2:mb-5
                prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-sm prose-p:md:text-base prose-p:leading-relaxed prose-p:mb-4 prose-p:text-foreground-700
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {relatedArticles.length > 0 && (
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                    相关文章
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.slug}
                        to={`/article/${related.slug}`}
                        className="group p-4 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-all"
                      >
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                          {related.title}
                        </h4>
                        <p className="text-xs text-foreground-500 line-clamp-2">
                          {related.excerpt}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}